/*
 * Service worker registration with a guard (docs/PWA.md): production builds only (`VITE_PWA=0` turns it off in a
 * build, `VITE_PWA=1` forces it, e.g. the PWA e2e stack), secure contexts only (https or localhost), never under
 * test runners (jsdom has no serviceWorker), and never when the page is framed. A waiting worker is announced to
 * the app (the update toast) and takes over only when asked.
 */

/** Production build, unless `VITE_PWA` says otherwise. */
export function pwaEnabled(prod: boolean, flag: string | undefined): boolean {
  if (flag === '1') return true
  if (flag === '0') return false
  return prod
}

export interface SwEnvironment {
  prod: boolean
  secureContext: boolean
  hasServiceWorker: boolean
  framed: boolean
  /** `?sw=off` or localStorage `algoboo.sw = off`: an escape hatch for support */
  optOut: boolean
}

export function shouldRegisterServiceWorker(e: SwEnvironment): boolean {
  return e.prod && e.secureContext && e.hasServiceWorker && !e.framed && !e.optOut
}

export function currentSwEnvironment(): SwEnvironment {
  let optOut = false
  try {
    optOut = new URLSearchParams(location.search).get('sw') === 'off' || localStorage.getItem('algoboo.sw') === 'off'
  } catch {
    // storage blocked
  }
  let framed: boolean
  try {
    framed = window.self !== window.top
  } catch {
    framed = true
  }
  return {
    prod: pwaEnabled(import.meta.env.PROD, import.meta.env.VITE_PWA),
    secureContext: window.isSecureContext,
    hasServiceWorker: typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
    framed,
    optOut,
  }
}

type Listener = (waiting: ServiceWorker) => void
const listeners = new Set<Listener>()
let waitingWorker: ServiceWorker | null = null

/** Subscribe to "a new version is ready"; fires at once if one is already waiting. */
export function onUpdateReady(fn: Listener): () => void {
  listeners.add(fn)
  if (waitingWorker) fn(waitingWorker)
  return () => {
    listeners.delete(fn)
  }
}

function announce(w: ServiceWorker) {
  waitingWorker = w
  for (const l of listeners) l(w)
}

/** Ask the waiting worker to take over, then reload once it controls the page. */
export function applyUpdate(w: ServiceWorker | null = waitingWorker): void {
  if (!w) return
  let reloaded = false
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloaded) return
    reloaded = true
    location.reload()
  })
  w.postMessage({ type: 'SKIP_WAITING' })
}

export async function registerServiceWorker(env: SwEnvironment = currentSwEnvironment()): Promise<ServiceWorkerRegistration | null> {
  if (!shouldRegisterServiceWorker(env)) return null
  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' })
    // a worker already waiting from an earlier visit, while this page is controlled by the old one
    if (reg.waiting && navigator.serviceWorker.controller) announce(reg.waiting)
    reg.addEventListener('updatefound', () => {
      const w = reg.installing
      if (!w) return
      w.addEventListener('statechange', () => {
        // first install has no controller: nothing to update
        if (w.state === 'installed' && navigator.serviceWorker.controller) announce(w)
      })
    })
    // look for a new version when the tab comes back and every 30 minutes
    const check = () => void reg.update().catch(() => undefined)
    document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && check())
    setInterval(check, 30 * 60_000)
    return reg
  } catch {
    return null
  }
}

/** Installed to the home screen (or launched from it). */
export function isStandalone(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches === true || (navigator as Navigator & { standalone?: boolean }).standalone === true
}
